import { ChangeDetectionStrategy, Component, DestroyRef, Injectable, OnInit, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { AppSettings, Settings } from 'app/app.settings';
import { AuthService } from '@core/auth';
import { ConfigService, LoggerService } from '@core/services';
import { ConfigSettings } from 'environments/ConfigSettings';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

@Component({
  templateUrl: './login-saml.component.html',
  styleUrls: ['./login-saml.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSidenavModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
@Injectable()
export class LoginSamlComponent implements OnInit {
  formGroup: FormGroup = new FormGroup({
    client: new FormControl('Development', [Validators.required, Validators.maxLength(250)]),
    saml: new FormControl('epolerecky@shiftdigital.com', [Validators.required]),
  });
  title = '';
  config!: ConfigSettings;
  settings: Settings;
  loading = false;

  constructor(
    public appSettings: AppSettings,
    private authService: AuthService,
    private router: Router,
    private logger: LoggerService,
    private configService: ConfigService,
    private destroyRef: DestroyRef
  ) {
    this.settings = this.appSettings.settings;
  }

  async ngOnInit(): Promise<void> {
    this.configService.Config.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((config) => {
      if (!config) return;
      this.config = config;
    });
  }

  public get getLogo(): string {
    return this.config?.logo;
  }

  public get getClient(): string {
    if (this.config?.environmentName?.includes('local')) {
      return `${this.config?.client} - ${this.config.environmentName}`;
    }
    return this.config?.client ?? '';
  }

  public get getLoginTitle(): string {
    return (this.config?.client ?? '') + ' ' + (this.config?.appName ?? '');
  }

  public get clientHasError(): boolean {
    return (this.formGroup.dirty || this.formGroup.touched) && (this.formGroup.get('client')?.invalid ?? false);
  }

  public get clientError(): string {
    return this.formGroup.get('client')?.hasError('required')
      ? 'You must enter a value'
      : this.formGroup.get('client')?.hasError('maxlength')
        ? 'Email cannot be more than 25 characters long'
        : '';
  }

  public get samlHasError(): boolean {
    return (this.formGroup.dirty || this.formGroup.touched) && (this.formGroup.get('saml')?.invalid ?? false);
  }

  public get samlError(): string {
    return this.formGroup.get('saml')?.hasError('required') ? 'You must enter a value' : '';
  }

  async login(): Promise<void> {
    this.loading = true;
    this.formGroup.disable();
    const client = this.formGroup.get('client')?.value;
    const saml = this.formGroup.get('saml')?.value;

    try {
      const login = await this.authService.samlLogin(client, saml);
      if (login?.successful) {
        this.logger.displaySuccess('Saml Login Successful', 'Login');
        this.afterLogin();
      } else {
        this.logger.displayError(login.message, 'Login');
      }
    } finally {
      this.loading = false;
      this.formGroup.enable();
    }
  }

  afterLogin(): void {
    const isRetailer = this.authService.isRetailerUser();
    let defaultRoute = '/dashboard/inbound';
    if (isRetailer) {
      defaultRoute = this.authService.isInboundRetailer() ? '/dashboard/inbound' : '/dashboard/outbound';
    }
    console.debug('Navigating to:', defaultRoute);
    this.settings.loadingSpinner = false;
    this.router.navigateByUrl(defaultRoute);
  }
}
